import { inject, Injectable, InjectionToken } from '@angular/core';

import { EnvironmentService } from '@core/config/environment.service';

export interface LoggerErrorReporter {
  captureError(message: string, error?: unknown): void;
}

export const LOGGER_ERROR_REPORTERS = new InjectionToken<readonly LoggerErrorReporter[]>(
  'LOGGER_ERROR_REPORTERS',
  { factory: () => [] },
);

/**
 * Central logging: verbose in dev, minimal noise in prod.
 * Register remote error reporting (Sentry, CloudWatch RUM, Datadog) via `LOGGER_ERROR_REPORTERS`.
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly env = inject(EnvironmentService);
  private readonly errorReporters = inject(LOGGER_ERROR_REPORTERS);

  debug(message: string, ...optional: unknown[]): void {
    if (!this.env.logging.debugEnabled) {
      return;
    }
    console.debug(`[Remmi] ${message}`, ...optional);
  }

  info(message: string, ...optional: unknown[]): void {
    if (this.env.production) {
      return;
    }
    console.info(`[Remmi] ${message}`, ...optional);
  }

  warn(message: string, ...optional: unknown[]): void {
    if (this.env.production) {
      return;
    }
    console.warn(`[Remmi] ${message}`, ...optional);
  }

  error(message: string, error?: unknown): void {
    console.error(`[Remmi] ${message}`, error);
    for (const reporter of this.errorReporters) {
      try {
        reporter.captureError(message, error);
      } catch {
        // Logging must never throw back into application code.
      }
    }
  }
}
